import React, { useState, useEffect } from 'react';
import { getAcceptedUser } from '../data/acceptedUsers';
import { db } from '../utils/firebase';
import { ref, set } from 'firebase/database';
import { useAuth } from '../AuthContext';

const EMAIL_KEY = 'fashion_gallery_user';

function safeKey(email) {
  return email.replace(/[.#$\[\]@]/g, '_');
}

export default function LoginScreen({ onLogin }) {  
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordText, setShowPasswordText] = useState(false);
  const [isAdminEmail, setIsAdminEmail] = useState(false);

  // Set document title for login page
  useEffect(() => {
    document.title = 'Sign In | Titled Works';
  }, []);

  // Check if email is admin when email changes
  const handleEmailChange = async (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    
    if (newEmail.trim()) {
      try {
        const user = await getAcceptedUser(newEmail.trim().toLowerCase());
        setIsAdminEmail(user?.admin || false);
        if (!user?.admin) {
          setShowPassword(false);
          setPassword('');
        }
      } catch (error) {
        setIsAdminEmail(false);
      }
    } else {
      setIsAdminEmail(false);
      setShowPassword(false);
      setPassword('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    const trimmedEmail = email.trim().toLowerCase();
    console.log('Attempting login with email:', trimmedEmail);
    
    try {
      // Check if user exists in /users
      const existingUser = await getAcceptedUser(trimmedEmail);
      console.log('Existing user found:', existingUser);
      
      if (existingUser) {
        if (existingUser.admin) {
          console.log('Admin user found');
          
          // Check if password is provided and correct
          if (!password.trim()) {
            setError('Password required for admin access.');
            setLoading(false);
            return;
          }
          
          // Validate admin password
          if (existingUser.password !== password) {
            setError('Incorrect password for admin access.');
            setLoading(false);
            return;
          }
          
          // Update presence in Realtime DB for admin
          console.log('Attempting to update presence...');
          await set(ref(db, `users/${safeKey(existingUser.email)}`), {
            name: existingUser.name,
            email: existingUser.email
          });
          
          login(existingUser); // Use login from context
          console.log("Admin login successful!");
        } else {
          // Non-admin user, just set the user object
          login(existingUser); // Use login from context
          console.log("User login successful!");
        }
      } else {
        // First time user - you might want to add them manually or have an admin interface
        console.log('User not found in /users');
        setError('Email not recognized. Please contact admin to be added to the system.');
      }
    } catch (error) {
      console.error('Error during login:', error);
      setError('Login failed. Please try again.');
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="rounded-lg p-8 max-w-md w-full">
        <h1 className="text-4xl mb-2 text-gray-900">Sign in to titled works sourcing</h1>
        <p className="text-gray-600 mb-6">
          {isAdminEmail ? 'Admin access requires password.' : 'Email access only. No password required.'}
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              id="email"
              type="email"
              autoFocus
              value={email}
              onChange={handleEmailChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
              placeholder="your@email.com"
              required
            />
          </div>
          {isAdminEmail && (
            <div className="relative">
              <input
                id="password"
                type={showPasswordText ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent pr-10"
                placeholder="Admin password"
                required
                autoFocus={isAdminEmail}
              />
              <button
                type="button"
                tabIndex={-1}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500"
                onClick={() => setShowPasswordText(v => !v)}
                aria-label={showPasswordText ? "Hide password" : "Show password"}
              >
                {showPasswordText ? (
                 <span className="text-xs">Hide</span>
                ) : (
                  <span className="text-xs">Show</span>
                )}
              </button>
            </div>
          )}
          {error && <div className="text-red-600 text-sm">{error}</div>}
          <button
            type="submit"
            className="btn-primary w-full mt-2 text-xs py-3"
            disabled={loading}
          >
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
} 